import axios from "axios";
import AuthService from "../AuthService";
import { isValidValue } from "utils/validation";
import logger from "utils/logger";

// A different API URL can be set on the /localUrl page (stored as "local"),
// but only on the dev server or when REACT_APP_ENABLE_LOCAL_URL is "true":
// in a production build a value left in localStorage must not redirect the
// app's requests (with the user's token) to another server
export const localUrlEnabled =
  import.meta.env.DEV || import.meta.env.REACT_APP_ENABLE_LOCAL_URL === "true";

const instance = axios.create({
  baseURL:
    (localUrlEnabled && localStorage.getItem("local")) ||
    import.meta.env.REACT_APP_API_URL,
});
instance.defaults.headers.common["content-language"] = "en";

// Request Interceptor
instance.interceptors.request.use(
  (config) => {
    try {
      const { token } = AuthService.getAccessToken();
      if (!!token) {
        config.headers["Authorization"] = token;
      }

      const userSession = AuthService.getUserSession();
      if (!!userSession) {
        config.headers.Authorization = userSession.session;
      }
    } catch (error) {
      logger.error("Error in request interceptor:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// One token refresh at a time: requests that get a 401 while a refresh is
// running wait for it instead of starting their own
let refreshing = null;
const refreshTokenOnce = () => {
  if (!refreshing) {
    refreshing = AuthService.refreshToken().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
};

// Response Interceptor
instance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error?.config?.url === "v1/auth/login/verify")
      return Promise.reject(error);
    if (
      error?.response?.status === 401 &&
      !isValidValue(originalRequest._retry) &&
      !originalRequest.url.includes("/refresh-token")
    ) {
      // Retried once: a second 401 is not refreshed again (no endless loop)
      originalRequest._retry = true;
      try {
        const token = await refreshTokenOnce();
        originalRequest.headers["Authorization"] = token;
        return instance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    if (axios.isCancel(error)) {
      error.__CANCEL__ = true;
    }
    return Promise.reject(error);
  },
);

export default instance;
